import { useState } from 'react';
import { toast } from 'sonner';
import { useCreateActivity } from '../../hooks/useActivities';
import UnsavedChangesModal from './UnsavedChangesModal';
import ActivityFormFields from './ActivityFormFields';
import { emptyActivityForm } from './activityForm';
import { useActivityForm } from './useActivityForm';

interface ActivityCreatePanelProps {
  onCancel: () => void;
  onCreated: (id: number) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export default function ActivityCreatePanel({ onCancel, onCreated, onDirtyChange }: ActivityCreatePanelProps) {
  const form = useActivityForm(emptyActivityForm, onDirtyChange);
  const [pendingCancel, setPendingCancel] = useState(false);
  const createMutation = useCreateActivity();

  const handleCreate = async () => {
    const payload = form.validate();
    if (!payload) return;
    try {
      const result = await createMutation.mutateAsync(payload);
      form.setIsDirty(false);
      toast.success('Activity created as draft');
      onCreated(result.id);
    } catch (err) {
      form.handleServerError(err, payload);
    }
  };

  const handleCancelClick = () => {
    if (form.isDirty) {
      setPendingCancel(true);
    } else {
      onCancel();
    }
  };

  return (
    <>
      <div data-testid="activity-create-panel" className="admin-card p-[30px] relative">
        <p className="uppercase tracking-[0.12em] font-bold text-pine-ink-soft text-[11px] mb-6">New activity</p>

        <ActivityFormFields
          formData={form.formData}
          onFieldChange={form.onFieldChange}
          onLevelChange={form.onLevelChange}
          errors={form.errors}
          imageValue={form.imageValue}
          onImageChange={form.onImageChange}
        />

        <div className="flex items-center justify-between mt-8 pt-4 border-t border-pine-line">
          <button
            type="button"
            onClick={handleCancelClick}
            className="px-4 py-2 text-sm text-pine-ink-soft hover:text-pine-ink border border-pine-line rounded-md hover:bg-pine-hover-item transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="create-activity-button"
            onClick={handleCreate}
            disabled={createMutation.isPending}
            className="px-5 py-2 text-sm font-semibold bg-pine-pine hover:bg-pine-pine-deep text-white rounded-md disabled:opacity-60 transition-colors"
          >
            {createMutation.isPending ? 'Creating…' : 'Create activity'}
          </button>
        </div>
      </div>

      <UnsavedChangesModal
        open={pendingCancel}
        onLeave={() => {
          setPendingCancel(false);
          form.setIsDirty(false);
          onCancel();
        }}
        onStay={() => setPendingCancel(false)}
      />
    </>
  );
}
